import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApiClientError,
  getTeacherLessonDetail,
  getTeacherLessonTests,
  postLessonAiGenerate,
  postLessonAiRefine,
  type LessonAiDraft,
  type LessonAiStandardsRequest,
  type LessonAiTestDraft,
  type LessonWorkspaceMode,
  type TeacherLessonRecord,
  type TeacherLessonTestVisibility,
} from "../../auth/api-client";
import { useAuth } from "../../auth/AuthContext";
import {
  DEFAULT_MAIN_PY,
  mainPyFromLesson,
  applyPep8IndentToLessonDraft,
  syncMainPyInStarterFiles,
  workspaceFieldsFromLesson,
} from "../course-template-helpers";
import { saveLessonDraftToLibrary } from "../saveLessonDraft";
import {
  buildAdvancedPromptContext,
  draftForSimpleModeRegenerate,
  stripAdvancedModeMetadata,
  type LessonAiAdvancedPromptContext,
} from "../lessonAiAdvancedModePrompt";
import {
  lessonAiPhaseAtElapsed,
  phasesForLessonAiMode,
  startLessonAiProgressClock,
  type LessonAiProgressUpdate,
} from "../lesson-ai-progress";
import { normalizePythonIndentation } from "../../student-ide/normalizePythonIndentation";
import { lessonCanMoveToDrafts } from "../lessonDraftRules";

export const EMPTY_DRAFT: LessonAiDraft = {
  title: "",
  workspaceMode: "simple",
  mainPy: DEFAULT_MAIN_PY,
  tourSteps: [
    { title: "Welcome", body: ["Read these steps while you work in the editor."], referenceCode: null },
  ],
  tests: [],
};

function draftFromDetail(
  lesson: {
    title: string;
    slug: string | null;
    description?: string | null;
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    workspaceMode?: LessonWorkspaceMode;
    starterFilesJson?: unknown;
  },
  mainPy: string,
  tourSteps: { title: string; body: string[]; referenceCode: string | null }[],
  tests: {
    name: string;
    description: string | null;
    testCode: string;
    isRequired: boolean;
    visibility: TeacherLessonTestVisibility;
  }[]
): LessonAiDraft {
  return {
    title: lesson.title,
    slug: lesson.slug ?? undefined,
    description: lesson.description ?? undefined,
    iconName: lesson.iconName ?? undefined,
    iconBgColor: lesson.iconBgColor ?? undefined,
    iconFgColor: lesson.iconFgColor ?? undefined,
    ...workspaceFieldsFromLesson(lesson, mainPy),
    mainPy: mainPy || DEFAULT_MAIN_PY,
    tourSteps: tourSteps.map((s) => ({
      title: s.title,
      body: s.body,
      referenceCode: s.referenceCode?.trim() ? normalizePythonIndentation(s.referenceCode) : s.referenceCode,
    })),
    tests: tests.map((t) => ({
      name: t.name,
      description: t.description ?? undefined,
      testCode: normalizePythonIndentation(t.testCode),
      isRequired: t.isRequired,
      visibility: t.visibility,
    })),
  };
}

function normalizeLessonAiDraft(d: LessonAiDraft): string {
  return JSON.stringify({
    title: d.title.trim(),
    slug: (d.slug ?? "").trim(),
    description: (d.description ?? "").trim(),
    iconName: d.iconName ?? "",
    iconBgColor: d.iconBgColor ?? "",
    iconFgColor: d.iconFgColor ?? "",
    workspaceMode: d.workspaceMode ?? "simple",
    mainPy: d.mainPy,
    starterFiles: d.starterFiles ?? null,
    tourSteps: d.tourSteps.map((s) => ({
      title: s.title,
      body: s.body,
      referenceCode: s.referenceCode ?? null,
    })),
    tests: d.tests.map((t) => ({
      name: t.name,
      description: t.description ?? null,
      testCode: t.testCode,
      isRequired: t.isRequired !== false,
      visibility: t.visibility ?? "detailed",
    })),
  });
}

function lessonDraftsEqual(a: LessonAiDraft, b: LessonAiDraft): boolean {
  return normalizeLessonAiDraft(a) === normalizeLessonAiDraft(b);
}

export interface UseLessonDraftResult {
  draft: LessonAiDraft;
  setDraft: React.Dispatch<React.SetStateAction<LessonAiDraft>>;
  updateStep: (index: number, patch: Partial<LessonAiDraft["tourSteps"][number]>) => void;
  updateTest: (index: number, patch: Partial<LessonAiTestDraft>) => void;
  prompt: string;
  setPrompt: (value: string) => void;
  isEvolvingExisting: boolean;
  savedLessonId: string | null;
  creationPrompt: string | null;
  hasUnsavedChanges: boolean;
  /** Last saved draft snapshot, used for published-lesson changelog diffs. */
  savedBaseline: LessonAiDraft;
  loadingLesson: boolean;
  aiBusy: boolean;
  aiProgressMode: "generate" | "refine" | null;
  aiProgress: LessonAiProgressUpdate | null;
  saving: boolean;
  error: string | null;
  setError: (value: string | null) => void;
  saveSuccess: string | null;
  setSaveSuccess: (value: string | null) => void;
  upgradeCta: string | null;
  advancedPrompt: LessonAiAdvancedPromptContext | null;
  advancedPromptBusy: boolean;
  communityPublishStatus: string | null;
  isDraft: boolean;
  setIsDraft: (value: boolean) => void;
  usedInTemplates: number;
  activeCohorts: number;
  canMarkAsDraft: boolean;
  handleGenerate: () => Promise<void>;
  handleRefine: () => Promise<void>;
  handleSave: (changelog?: string | null) => Promise<void>;
  handleAcceptAdvancedMode: () => void;
  handlePreferSimpleMode: () => Promise<void>;
  confirmLeaveWithoutSaving: () => boolean;
  guardedNavigate: (to: string) => void;
}

export function useLessonDraft(
  existingLessonId: string | undefined,
  options?: {
    afterSave?: (lessonId: string) => Promise<void>;
    getAiPrompt?: () => string;
    isAiPromptEmpty?: () => boolean;
    getStandardsRequest?: () => LessonAiStandardsRequest | undefined;
    onAiAlignment?: (standardCodes: string[], glossaryTerms: string[]) => void;
    onGenerateClearPrompt?: () => void;
    onDraftSaved?: () => void;
  }
): UseLessonDraftResult {
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const afterSave = options?.afterSave;
  const getAiPrompt = options?.getAiPrompt;
  const isAiPromptEmpty = options?.isAiPromptEmpty;
  const getStandardsRequest = options?.getStandardsRequest;
  const onAiAlignment = options?.onAiAlignment;
  const onGenerateClearPrompt = options?.onGenerateClearPrompt;
  const onDraftSaved = options?.onDraftSaved;

  const [draft, setDraft] = useState<LessonAiDraft>(EMPTY_DRAFT);
  const [savedBaseline, setSavedBaseline] = useState<LessonAiDraft>(EMPTY_DRAFT);
  const [prompt, setPrompt] = useState("");
  const [loadingLesson, setLoadingLesson] = useState(!!existingLessonId);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiProgressMode, setAiProgressMode] = useState<"generate" | "refine" | null>(null);
  const [aiProgress, setAiProgress] = useState<LessonAiProgressUpdate | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedLessonId, setSavedLessonId] = useState<string | null>(existingLessonId ?? null);
  const [creationPrompt, setCreationPrompt] = useState<string | null>(null);
  const [communityPublishStatus, setCommunityPublishStatus] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [usedInTemplates, setUsedInTemplates] = useState(0);
  const [activeCohorts, setActiveCohorts] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [upgradeCta, setUpgradeCta] = useState<string | null>(null);
  const [advancedPrompt, setAdvancedPrompt] = useState<LessonAiAdvancedPromptContext | null>(null);
  const [advancedPromptBusy, setAdvancedPromptBusy] = useState(false);

  const isEvolvingExisting = Boolean(existingLessonId);

  const hasUnsavedChanges = useMemo(
    () => !lessonDraftsEqual(draft, savedBaseline),
    [draft, savedBaseline]
  );

  const canMarkAsDraft = useMemo(
    () =>
      lessonCanMoveToDrafts({
        communityPublishStatus: communityPublishStatus as TeacherLessonRecord["communityPublishStatus"],
        usedInTemplates,
        activeCohorts,
      }),
    [communityPublishStatus, usedInTemplates, activeCohorts]
  );

  const leaveWithoutSavingMessage = isEvolvingExisting
    ? "You have unsaved changes to this lesson. Leave without saving?"
    : "You have not saved this lesson yet. Leave and discard your work?";

  const loadExisting = useCallback(async () => {
    if (!bearer || !existingLessonId) return;
    setLoadingLesson(true);
    setError(null);
    try {
      const [detail, testsPayload] = await Promise.all([
        getTeacherLessonDetail(bearer, undefined, existingLessonId),
        getTeacherLessonTests(bearer, undefined, existingLessonId),
      ]);
      const mp = detail.preview?.mainPy?.trim() ? detail.preview.mainPy : mainPyFromLesson(detail.lesson);
      const mainPy = mp || DEFAULT_MAIN_PY;
      const loadedDraft = draftFromDetail(
        detail.lesson,
        mainPy,
        detail.tourSteps,
        testsPayload.tests.map((t) => ({
          name: t.name,
          description: t.description,
          testCode: t.testCode ?? "",
          isRequired: t.isRequired,
          visibility: t.visibility,
        }))
      );
      setDraft(loadedDraft);
      setSavedBaseline(loadedDraft);
      setSavedLessonId(existingLessonId);
      setCreationPrompt(detail.lesson.creationPrompt ?? null);
      setCommunityPublishStatus(detail.lesson.communityPublishStatus ?? null);
      setIsDraft(detail.lesson.isDraft === true);
      setUsedInTemplates(detail.usedInTemplates ?? 0);
      setActiveCohorts(detail.activeCohorts ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load lesson");
    } finally {
      setLoadingLesson(false);
    }
  }, [bearer, existingLessonId]);

  useEffect(() => {
    void loadExisting();
  }, [loadExisting]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (saveSuccess && hasUnsavedChanges) setSaveSuccess(null);
  }, [saveSuccess, hasUnsavedChanges]);

  const confirmLeaveWithoutSaving = useCallback((): boolean => {
    if (!hasUnsavedChanges) return true;
    return window.confirm(leaveWithoutSavingMessage);
  }, [hasUnsavedChanges, leaveWithoutSavingMessage]);

  const guardedNavigate = useCallback(
    (to: string) => {
      if (!confirmLeaveWithoutSaving()) return;
      navigate(to);
    },
    [confirmLeaveWithoutSaving, navigate]
  );

  const updateStep = useCallback(
    (index: number, patch: Partial<LessonAiDraft["tourSteps"][number]>) => {
      setDraft((d) => ({
        ...d,
        tourSteps: d.tourSteps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
      }));
    },
    []
  );

  const updateTest = useCallback((index: number, patch: Partial<LessonAiTestDraft>) => {
    setDraft((d) => ({
      ...d,
      tests: d.tests.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  }, []);

  async function runWithAiProgress<T>(
    mode: "generate" | "refine",
    work: () => Promise<T>
  ): Promise<T | undefined> {
    setAiBusy(true);
    setAiProgressMode(mode);
    setAiProgress(lessonAiPhaseAtElapsed(phasesForLessonAiMode(mode), 0));
    const stopClock = startLessonAiProgressClock(mode, setAiProgress);
    try {
      return await work();
    } finally {
      stopClock();
      setAiBusy(false);
      setAiProgressMode(null);
      setAiProgress(null);
    }
  }

  function commitAiDraft(
    result: LessonAiDraft,
    meta: { sourcePrompt: string; kind: "generate" | "refine"; seed?: LessonAiDraft }
  ) {
    const normalized = applyPep8IndentToLessonDraft(result);
    const promptCtx = buildAdvancedPromptContext(normalized, meta);
    if (promptCtx) {
      setAdvancedPrompt(promptCtx);
      return;
    }
    setDraft(stripAdvancedModeMetadata(normalized));
    if (meta.kind === "generate") {
      setCreationPrompt(meta.sourcePrompt);
      setPrompt("");
      onGenerateClearPrompt?.();
    }
    setSaveSuccess(null);
  }

  function handleAcceptAdvancedMode() {
    if (!advancedPrompt) return;
    const pending = advancedPrompt.pendingDraft;
    setDraft(
      stripAdvancedModeMetadata({
        ...pending,
        starterFiles: pending.starterFiles?.length
          ? syncMainPyInStarterFiles(pending.starterFiles, pending.mainPy)
          : syncMainPyInStarterFiles([], pending.mainPy),
      })
    );
    if (advancedPrompt.kind === "generate") {
      setCreationPrompt(advancedPrompt.sourcePrompt);
      setPrompt("");
      onGenerateClearPrompt?.();
    }
    setAdvancedPrompt(null);
    setSaveSuccess(null);
  }

  async function handlePreferSimpleMode() {
    if (!bearer || !advancedPrompt) return;
    const ctx = advancedPrompt;
    setAdvancedPromptBusy(true);
    setError(null);
    try {
      const regenerateDraft = draftForSimpleModeRegenerate(ctx.pendingDraft);
      const result =
        ctx.kind === "generate"
          ? await runWithAiProgress("generate", () =>
              postLessonAiGenerate(bearer, {
                prompt: ctx.sourcePrompt,
                draft: regenerateDraft,
                forceWorkspaceMode: "simple",
              })
            )
          : await runWithAiProgress("refine", () =>
              postLessonAiRefine(bearer, {
                prompt: `${ctx.sourcePrompt}\n\nUse simple mode only (single main.py).`,
                draft: regenerateDraft,
                forceWorkspaceMode: "simple",
              })
            );
      if (!result) return;
      setDraft(stripAdvancedModeMetadata(applyPep8IndentToLessonDraft(result.draft)));
      if (ctx.kind === "generate") {
        setCreationPrompt(ctx.sourcePrompt);
        setPrompt("");
      }
      setAdvancedPrompt(null);
      setSaveSuccess(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not regenerate for simple mode";
      setError(msg);
      if (e instanceof ApiClientError) {
        if (e.code === "BILLING_REQUIRED") setUpgradeCta("Manage billing to continue using AI lesson tools.");
        else if (e.code === "AI_LIMIT_REACHED") setUpgradeCta("See your AI usage on the billing page.");
      }
    } finally {
      setAdvancedPromptBusy(false);
    }
  }

  async function handleGenerate() {
    const promptText = (getAiPrompt?.() ?? prompt).trim();
    if (!bearer || (isAiPromptEmpty ? isAiPromptEmpty() : !promptText)) return;
    setError(null);
    setUpgradeCta(null);
    try {
      const sourcePrompt = promptText;
      const seed =
        draft.title.trim() || draft.mainPy.trim() !== DEFAULT_MAIN_PY.trim() ? draft : undefined;
      const standardsReq = getStandardsRequest?.();
      const result = await runWithAiProgress("generate", () =>
        postLessonAiGenerate(bearer, {
          prompt: sourcePrompt,
          ...(seed ? { draft: seed } : {}),
          ...(standardsReq ?? {}),
        })
      );
      if (!result) return;
      onAiAlignment?.(result.draft.alignedStandardCodes ?? [], result.draft.alignedGlossaryTerms ?? []);
      commitAiDraft(result.draft, { sourcePrompt, kind: "generate", seed });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Generation failed";
      setError(msg);
      if (e instanceof ApiClientError) {
        if (e.code === "BILLING_REQUIRED") setUpgradeCta("Manage billing to continue using AI lesson tools.");
        else if (e.code === "AI_LIMIT_REACHED") setUpgradeCta("See your AI usage on the billing page.");
      }
    }
  }

  async function handleRefine() {
    const promptText = (getAiPrompt?.() ?? prompt).trim();
    if (!bearer || (isAiPromptEmpty ? isAiPromptEmpty() : !promptText)) return;
    if (!draft.title.trim()) {
      setError("Set a lesson title before refining.");
      return;
    }
    setError(null);
    setUpgradeCta(null);
    try {
      const sourcePrompt = promptText;
      const standardsReq = getStandardsRequest?.();
      const result = await runWithAiProgress("refine", () =>
        postLessonAiRefine(bearer, { prompt: sourcePrompt, draft, ...(standardsReq ?? {}) })
      );
      if (!result) return;
      onAiAlignment?.(result.draft.alignedStandardCodes ?? [], result.draft.alignedGlossaryTerms ?? []);
      commitAiDraft(result.draft, { sourcePrompt, kind: "refine" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Refinement failed";
      setError(msg);
      if (e instanceof ApiClientError) {
        if (e.code === "BILLING_REQUIRED") setUpgradeCta("Manage billing to continue using AI lesson tools.");
        else if (e.code === "AI_LIMIT_REACHED") setUpgradeCta("See your AI usage on the billing page.");
      }
    }
  }

  async function handleSave(changelog?: string | null) {
    if (!bearer || !draft.title.trim()) {
      setError("Lesson title is required before saving.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const lessonId = await saveLessonDraftToLibrary(bearer, draft, {
        lessonId: savedLessonId,
        creationPrompt,
        changelog: changelog ?? null,
        isDraft: isDraft ? true : false,
      });
      setSavedBaseline(draft);

      if (afterSave) {
        try {
          await afterSave(lessonId);
        } catch (e) {
          const msg = e instanceof Error ? e.message : "Failed to save standards links";
          setError(`Lesson saved, but standards could not be updated: ${msg}`);
          setSaveSuccess(null);
          setSavedLessonId(lessonId);
          if (!savedLessonId) {
            navigate(`/teacher/lessons/${lessonId}`, { replace: true });
          }
          return;
        }
      }

      if (savedLessonId) {
        if (isDraft) {
          onDraftSaved?.();
        } else {
          setSaveSuccess(
            "Lesson saved to your library. Templates and future assignments use the new content."
          );
        }
        if (communityPublishStatus === "approved" || communityPublishStatus === "pending_review") {
          setCommunityPublishStatus("pending_review");
        }
        setError(null);
      } else {
        setSavedLessonId(lessonId);
        if (isDraft) {
          onDraftSaved?.();
        }
        navigate(`/teacher/lessons/${lessonId}`, { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setSaveSuccess(null);
    } finally {
      setSaving(false);
    }
  }

  return {
    draft,
    setDraft,
    updateStep,
    updateTest,
    prompt,
    setPrompt,
    isEvolvingExisting,
    savedLessonId,
    creationPrompt,
    hasUnsavedChanges,
    savedBaseline,
    loadingLesson,
    aiBusy,
    aiProgressMode,
    aiProgress,
    saving,
    error,
    setError,
    saveSuccess,
    setSaveSuccess,
    upgradeCta,
    advancedPrompt,
    advancedPromptBusy,
    communityPublishStatus,
    isDraft,
    setIsDraft,
    usedInTemplates,
    activeCohorts,
    canMarkAsDraft,
    handleGenerate,
    handleRefine,
    handleSave,
    handleAcceptAdvancedMode,
    handlePreferSimpleMode,
    confirmLeaveWithoutSaving,
    guardedNavigate,
  };
}
